if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        // Check if subscribe function exists before using it
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.onVariantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );
        }

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
          }
        }
      }

      disconnectedCallback() {
        if (this.onVariantChangeUnsubscriber) {
          this.onVariantChangeUnsubscriber();
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          productForm.toggleSubmitButton(true);
        }
        if (productForm && typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage();
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);
          
          // Filter variant images based on metafield
          this.filterVariantImages(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          
          // Update the product title with variant color
          updateSourceFromDestination('ProductTitle');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          }
        };
      }

      // Filter variant images based on custom.name_of_images metafield
      filterVariantImages(variant) {
        if (!variant || !variant.featured_image) return;

        const featuredUrl = variant.featured_image.src;
        const segments = featuredUrl.split('/');
        const fileName = segments[segments.length - 1];
        const fileNameWithoutQuery = fileName.split('?')[0];
        const fileNameWithoutExt = fileNameWithoutQuery.replace(/\.[^/.]+$/, '').toLowerCase();

        console.log('Filtering images for variant:', fileNameWithoutExt);

        // Find all elements with data-img-names attribute (main slides, thumbnails, dots)
        const imageElements = document.querySelectorAll('[data-img-names]');
        
        if (imageElements.length === 0) {
          console.log('No elements with data-img-names found');
          return;
        }

        let firstVisibleSlide = null;
        let visibleCount = 0;

        imageElements.forEach((element) => {
          const imgNames = element.getAttribute('data-img-names');
          
          if (imgNames && imgNames.toLowerCase().includes(fileNameWithoutExt)) {
            // Show this element
            element.style.display = '';
            element.classList.remove('hidden');
            
            // Track first visible main slide
            if (!firstVisibleSlide && element.classList.contains('product__media-item')) {
              firstVisibleSlide = element;
            }
            if (element.classList.contains('product__media-item')) {
              visibleCount++;
            }
          } else if (imgNames) {
            // Hide this element (only if it has data-img-names)
            element.style.display = 'none';
          }
        });

        console.log('Visible slides:', visibleCount);

        // Update the first visible slide to be active
        if (firstVisibleSlide) {
          // Remove is-active from all slides
          document.querySelectorAll('.product__media-item').forEach(slide => {
            slide.classList.remove('is-active');
          });
          
          // Add is-active to first visible
          firstVisibleSlide.classList.add('is-active');

          // Update slide counter
          const counter = document.querySelector('.slider-counter--current');
          if (counter) counter.textContent = '1';
          
          // Update total counter to show only visible slides
          const totalCounter = document.querySelector('.slider-counter--total');
          if (totalCounter && visibleCount > 0) {
            totalCounter.textContent = visibleCount;
          }

          // Reset slider position
          const slider = firstVisibleSlide.closest('.slider, .product__media-list');
          if (slider) {
            slider.scrollLeft = 0;
          }
        }

        // Update first visible dot to be active
        const dots = document.querySelectorAll('.slider-pagination .dot');
        dots.forEach(dot => dot.classList.remove('active'));
        const firstVisibleDot = Array.from(dots).find(dot => {
          if (dot.style.display === 'none') return false;
          const imgNames = dot.getAttribute('data-img-names');
          return imgNames && imgNames.toLowerCase().includes(fileNameWithoutExt);
        });
        if (firstVisibleDot) {
          firstVisibleDot.classList.add('active');
        }

        // Update thumbnails active state
        const thumbnails = document.querySelectorAll('.thumbnail-list__item');
        thumbnails.forEach(thumb => {
          const button = thumb.querySelector('button');
          if (button) {
            button.removeAttribute('aria-current');
          }
        });
        const firstVisibleThumb = Array.from(thumbnails).find(thumb => {
          if (thumb.style.display === 'none') return false;
          const imgNames = thumb.getAttribute('data-img-names');
          return imgNames && imgNames.toLowerCase().includes(fileNameWithoutExt);
        });
        if (firstVisibleThumb) {
          const button = firstVisibleThumb.querySelector('button');
          if (button) {
            button.setAttribute('aria-current', 'true');
          }
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover') && typeof enableZoomOnHover === 'function') {
            enableZoomOnHover(2);
          }
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        if (!this.quantityInput) return;
        
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');
        
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            if (spinner) spinner.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;
        
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm?.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

// Standalone variant image filter that works with direct DOM events
(function() {
  document.addEventListener('DOMContentLoaded', function() {
    // Listen for variant changes via clicks on color swatches
    document.addEventListener('change', function(e) {
      const target = e.target;
      
      // Check if this is a color variant selector
      if (target.matches('input[type="radio"][name*="Color"], input[type="radio"][name*="color"], select[name*="Color"], select[name*="color"]')) {
        // Small delay to allow the page to update
        setTimeout(function() {
          filterVariantImagesByCurrentSelection();
        }, 300);
      }
    });

    // Also listen for clicks on swatch elements
    document.addEventListener('click', function(e) {
      const swatch = e.target.closest('.swatch, .color-swatch, [data-option-value]');
      if (swatch) {
        setTimeout(function() {
          filterVariantImagesByCurrentSelection();
        }, 300);
      }
    });
  });

  function filterVariantImagesByCurrentSelection() {
    // Find the currently selected variant input
    const variantInput = document.querySelector('input[name="id"]');
    if (!variantInput) return;

    // Try to find the variant data
    const productInfoElement = document.querySelector('product-info');
    if (!productInfoElement) return;

    // Look for the selected variant data
    const selectedVariantData = document.querySelector('variant-selects [data-selected-variant]');
    if (selectedVariantData) {
      try {
        const variant = JSON.parse(selectedVariantData.innerHTML);
        if (variant && variant.featured_image) {
          filterImagesByFeaturedImage(variant.featured_image.src);
        }
      } catch (e) {
        console.log('Could not parse variant data');
      }
    }
  }

  function filterImagesByFeaturedImage(featuredUrl) {
    const segments = featuredUrl.split('/');
    const fileName = segments[segments.length - 1];
    const fileNameWithoutQuery = fileName.split('?')[0];
    const fileNameWithoutExt = fileNameWithoutQuery.replace(/\.[^/.]+$/, '').toLowerCase();

    const imageElements = document.querySelectorAll('[data-img-names]');
    
    if (imageElements.length === 0) return;

    let firstVisibleSlide = null;
    let visibleCount = 0;

    imageElements.forEach(function(element) {
      const imgNames = element.getAttribute('data-img-names');
      
      if (imgNames && imgNames.toLowerCase().includes(fileNameWithoutExt)) {
        element.style.display = '';
        element.classList.remove('hidden');
        
        if (!firstVisibleSlide && element.classList.contains('product__media-item')) {
          firstVisibleSlide = element;
        }
        if (element.classList.contains('product__media-item')) {
          visibleCount++;
        }
      } else if (imgNames) {
        element.style.display = 'none';
      }
    });

    if (firstVisibleSlide) {
      document.querySelectorAll('.product__media-item').forEach(function(slide) {
        slide.classList.remove('is-active');
      });
      
      firstVisibleSlide.classList.add('is-active');

      const counter = document.querySelector('.slider-counter--current');
      if (counter) counter.textContent = '1';
      
      const totalCounter = document.querySelector('.slider-counter--total');
      if (totalCounter && visibleCount > 0) {
        totalCounter.textContent = visibleCount;
      }

      const slider = firstVisibleSlide.closest('.slider, .product__media-list');
      if (slider) {
        slider.scrollLeft = 0;
      }
    }

    // Update dots
    const dots = document.querySelectorAll('.slider-pagination .dot');
    dots.forEach(function(dot) { dot.classList.remove('active'); });
    const visibleDots = Array.from(dots).filter(function(dot) {
      return dot.style.display !== 'none';
    });
    if (visibleDots.length > 0) {
      visibleDots[0].classList.add('active');
    }
  }
})();
